/* [ START OF MODULE - VER 1.1 (src/components/Roadmap/constants.js) ] */

/**
 * Shared constants for the Roadmap module.
 *
 * Extracted from RoadmapPage.jsx VER 3.0 (BUILD-2026.282) in BUILD-2026.283
 * as part of the RoadmapPage refactor — pure data, no dependencies, no JSX.
 *
 * Imported by RoadmapPage and every Roadmap subcomponent.
 */

// ─────────────────────────────────────────────────────────────────────────────
// TAG_META — visual config for each item tag. Used by Badge (in badges.jsx),
// the AddItemForm tag dropdown, RoadmapCard inline tag editor, and
// AISuggestionPanel suggestion list.
// ─────────────────────────────────────────────────────────────────────────────
export const TAG_META = {
  feature:     { label: 'Feature',     color: 'var(--accent-tech)',    bg: 'var(--tint-tech)',    border: 'var(--tint-tech-border)' },
  improvement: { label: 'Improvement', color: 'var(--accent-success)', bg: 'var(--tint-success)', border: 'var(--tint-success-border)' },
  bug:         { label: 'Bug Fix',     color: 'var(--accent-danger)',  bg: 'var(--tint-danger)',  border: 'var(--tint-danger-border)' },
  infra:       { label: 'Infra',       color: 'var(--text-secondary)', bg: 'var(--bg-hover)',     border: 'var(--border-color)' },
  design:      { label: 'Design',      color: 'var(--accent-primary)', bg: 'var(--tint-primary)', border: 'var(--tint-primary-border)' },
};

// ─────────────────────────────────────────────────────────────────────────────
// STATUS_META — visual config for each item status. Same consumer list as
// TAG_META above plus the section dividers and status jump pills.
// ─────────────────────────────────────────────────────────────────────────────
export const STATUS_META = {
  planned:            { label: 'Planned',           dot: 'var(--text-muted)',     bg: 'var(--bg-hover)',     border: 'var(--border-color)',        color: 'var(--text-secondary)' },
  'in-progress':      { label: 'In Progress',       dot: 'var(--accent-tech)',    bg: 'var(--tint-tech)',    border: 'var(--tint-tech-border)',    color: 'var(--accent-tech)' },
  'pending-approval': { label: 'Pending Approval',  dot: 'var(--accent-primary)', bg: 'var(--tint-primary)', border: 'var(--tint-primary-border)', color: 'var(--accent-primary)' },
  'on-hold':          { label: 'On Hold',           dot: 'var(--accent-warning)', bg: 'var(--tint-warning)', border: 'var(--tint-warning-border)', color: 'var(--accent-warning)' },
  'rolled-back':      { label: 'Rolled Back',       dot: 'var(--accent-danger)',  bg: 'var(--tint-danger)',  border: 'var(--tint-danger-border)',  color: 'var(--accent-danger)' },
  done:               { label: 'Done',              dot: 'var(--accent-success)', bg: 'var(--tint-success)', border: 'var(--tint-success-border)', color: 'var(--accent-success)' },
};

// STATUS_ORDER — display order for status sections in the list pane and the
// status jump pills in the desktop header. pending-approval sits second so
// shipped-but-unverified items surface at the top of the user's attention.
export const STATUS_ORDER = ['in-progress', 'pending-approval', 'planned', 'on-hold', 'rolled-back', 'done'];

// STATUS_CYCLE — order used by the legacy cycle-status action (no longer
// surfaced in the UI as of VER 2.2; kept for cycleStatus handler compatibility).
export const STATUS_CYCLE = ['planned', 'in-progress', 'done', 'on-hold'];

// STATUS_ALL — used by the in-card status dropdown and the desktop filter chips
// so users can directly select any status, including 'rolled-back' which is
// not reachable via STATUS_CYCLE. pending-approval is included so the user
// can move an item back to in-progress (or directly to done) from the card.
export const STATUS_ALL = ['planned', 'in-progress', 'pending-approval', 'on-hold', 'rolled-back', 'done'];

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE_BREAKPOINT — below this width (in px), RoadmapPage switches to a
// stacked single-pane layout. Consumed by useIsMobile.
// ─────────────────────────────────────────────────────────────────────────────
export const MOBILE_BREAKPOINT = 768;

// ─────────────────────────────────────────────────────────────────────────────
// btnBase — shared style object for low-emphasis action buttons in RoadmapCard
// (Edit, Remove, status dropdown trigger). Kept here so the same base style
// can be reused by future Roadmap subcomponents without duplicating literals.
// ─────────────────────────────────────────────────────────────────────────────
export const btnBase = {
  display: 'flex', alignItems: 'center', gap: 5,
  padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border-color)',
  background: 'transparent', color: 'var(--text-secondary)',
  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em',
  textTransform: 'uppercase', cursor: 'pointer', transition: '0.12s',
};

/*
================================================================================
CHANGE MANIFEST
================================================================================
VER 1.1 -- BUILD-2026.288 -- 2026-05-26 -- RMI-2026-120
- ADD: 'pending-approval' status as a first-class entry.
  * STATUS_META entry uses --accent-primary (brand purple) to signal
    "shipped, awaiting user verification" — distinct from --accent-warning
    (on-hold) so the two states never visually collide.
  * STATUS_ORDER inserts pending-approval immediately after in-progress so
    the user's attention queue sits at the top of the list pane.
  * STATUS_ALL adds it so the in-card status dropdown can target it and the
    desktop filter chips show it.
  * STATUS_CYCLE intentionally left alone — pending-approval should only be
    set deliberately at ship time, never reached by the legacy cycle button.
  * AddItemForm dropdown (which hardcodes ['planned','in-progress','on-hold'])
    also intentionally left alone — you never create an item already shipped.
- Why: the roadmap workflow has been using pending-approval via direct DB
  PATCH for shipped-but-unverified items since BUILD-265 era, but the UI
  had no entry for the string, so 3 items (RMI-091, RMI-116, RMI-118) were
  invisible in the kanban after their builds shipped.
================================================================================
VER 1.0 -- BUILD-2026.283 -- 2026-05-25 -- Extraction from RoadmapPage.jsx
- New file. Holds TAG_META, STATUS_META, STATUS_ORDER, STATUS_CYCLE, STATUS_ALL,
  MOBILE_BREAKPOINT, and btnBase. All previously defined as module-private
  constants in src/pages/RoadmapPage.jsx VER 3.0.
- Pure data. Zero behavior change. Imported everywhere those constants were
  previously referenced (RoadmapPage, RoadmapRow, RoadmapCard, AddItemForm,
  AISuggestionPanel, badges.jsx).
================================================================================
*/
/* [ END OF MODULE - VER 1.1 (src/components/Roadmap/constants.js) ] */
